// Issue states in the console (D100): the menus behind Ignore and Assign, the line that says
// where an issue stands ("Fixed in 5.2 (812)", "Regressed in 5.3"), why a change did not go
// through, and the issue page's actions. Releases and fix versions are the app's own text:
// they are only ever rendered as text, never translated.
import { useQuery } from '@tanstack/react-query'
import { type FormEvent, type ReactNode, useEffect, useId, useRef, useState } from 'react'
import { isApiError } from '../api/client'
import { appsQuery, useSetIssueState, windowReleasesQuery } from '../api/queries'
import type { IssueState, IssueStateChange } from '../api/types'
import { type Translator, useI18n } from '../i18n'
import { initials } from '../lib/format'
import { type IgnorePreset, ignoreChange, REOPEN, RESOLVE_NEXT } from '../lib/issues'
import { shortcutOff } from '../lib/keys'
import { releasesNewestFirst } from '../lib/releases'
import { Icon } from './icons'

export interface MenuItem {
  key: string
  label: string
  onSelect: () => void
  /** The label is someone's name or the app's text: never translated. */
  verbatim?: boolean
}

/**
 * A button that opens a short list of actions below it (a disclosure, as the user menu).
 * Escape or a click elsewhere closes it; the list opens toward the side with more room.
 */
export function ActionMenu({
  label,
  items,
  disabled,
  small = true,
  shortcut,
  icon,
  solid = false,
}: {
  label: ReactNode
  items: MenuItem[]
  disabled?: boolean
  /** The list's 28 px button; the issue page's are 34 px. */
  small?: boolean
  /** The view's primary action (Hand off to agent). */
  solid?: boolean
  /** A key that opens the menu (`a` assigns on the issue page). */
  shortcut?: string
  icon?: ReactNode
}) {
  const [open, setOpen] = useState(false)
  const [end, setEnd] = useState(false)
  const id = useId()
  const wrap = useRef<HTMLDivElement>(null)
  const button = useRef<HTMLButtonElement>(null)
  const list = useRef<HTMLUListElement>(null)
  const toggle = () => {
    const r = button.current?.getBoundingClientRect()
    setEnd(!!r && r.left > window.innerWidth / 2)
    setOpen((o) => !o)
  }
  useEffect(() => {
    if (!shortcut) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== shortcut || shortcutOff(e) || button.current?.disabled) return
      e.preventDefault()
      toggle()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  })
  // Opened from the keyboard, the first item takes focus.
  useEffect(() => {
    if (open) list.current?.querySelector('button')?.focus()
  }, [open])
  useEffect(() => {
    if (!open) return
    const away = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false)
    }
    const onEscape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      setOpen(false)
      if (wrap.current?.contains(document.activeElement)) button.current?.focus()
    }
    document.addEventListener('mousedown', away)
    document.addEventListener('keydown', onEscape)
    return () => {
      document.removeEventListener('mousedown', away)
      document.removeEventListener('keydown', onEscape)
    }
  }, [open])
  return (
    <div className="menu-wrap" ref={wrap}>
      <button
        ref={button}
        type="button"
        className={`btn${small ? ' sm' : ''}${solid ? ' solid' : ''}`}
        aria-expanded={open}
        aria-controls={id}
        aria-keyshortcuts={shortcut}
        disabled={disabled}
        onClick={toggle}
      >
        {icon}
        {label}
        {shortcut ? (
          <span aria-hidden="true">
            <kbd translate="no">{shortcut}</kbd>
          </span>
        ) : null}
        <Icon name="chev" small />
      </button>
      {open ? (
        <ul ref={list} className={`menu${end ? ' end' : ''}`} id={id}>
          {items.map((item) => (
            <li key={item.key}>
              <button
                type="button"
                {...(item.verbatim ? { translate: 'no' as const } : {})}
                onClick={() => {
                  setOpen(false)
                  button.current?.focus()
                  item.onSelect()
                }}
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

/** The ignores offered: the list's bulk bar takes the first three, the issue page all. */
export const IGNORE_PRESETS: { key: string; preset: IgnorePreset }[] = [
  { key: 'forever', preset: { mode: 'forever' } },
  { key: 'count100', preset: { mode: 'count', count: 100 } },
  { key: 'day', preset: { mode: 'until', hours: 24 } },
  { key: 'count1000', preset: { mode: 'count', count: 1000 } },
  { key: 'hour', preset: { mode: 'until', hours: 1 } },
  { key: 'week', preset: { mode: 'until', hours: 24 * 7 } },
]

export function ignoreLabel(t: Translator['t'], preset: IgnorePreset): string {
  if (preset.mode === 'count') return t('issues.state.ignore.count', { count: preset.count })
  if (preset.mode === 'until')
    return preset.hours >= 48
      ? t('issues.state.ignore.days', { days: preset.hours / 24 })
      : t('issues.state.ignore.hours', { hours: preset.hours })
  return t('issues.state.ignore.forever')
}

/** Why a change did not go through, in words a person can act on. */
export function stateErrorMessage(t: Translator['t'], error: unknown): string {
  if (isApiError(error, 429)) return t('issues.state.error.rateLimited')
  if (isApiError(error, 403)) return t('issues.state.error.forbidden')
  if (isApiError(error, 400)) return t('issues.state.error.invalid')
  if (isApiError(error, 503)) return t('issues.state.error.unavailable')
  return t('issues.state.error.failed')
}

export interface Members {
  /** Names by user id; empty while loading. */
  names: ReadonlyMap<string, string>
  list: { id: string; name: string }[]
}

/** The organization's members as the menus and lines name them (a name, or the email). */
export function membersOf(
  members: { user_id: string; name: string; email: string }[] | undefined,
): Members {
  const list = (members ?? []).map((m) => ({ id: m.user_id, name: m.name || m.email }))
  return { names: new Map(list.map((m) => [m.id, m.name])), list }
}

/** Where an issue stands, in one line: resolved, regressed, ignored, and who has it. */
export function stateLine(
  i18n: Pick<Translator, 't' | 'date' | 'number'>,
  state: IssueState | undefined,
  members: Members,
): string[] {
  const { t, date, number } = i18n
  const parts: string[] = []
  if (!state || state.status === 'unresolved') {
    if (state?.regressed_release)
      parts.push(
        state.resolved_release
          ? t('issues.state.line.regressedFrom', {
              release: state.regressed_release,
              fixed: state.resolved_release,
            })
          : t('issues.state.line.regressed', { release: state.regressed_release }),
      )
    else parts.push(t('issues.state.line.unresolved'))
  } else if (state.status === 'resolved') {
    parts.push(
      state.resolved_release
        ? t('issues.state.line.fixed', { release: state.resolved_release })
        : t('issues.state.line.next'),
    )
  } else if (state.ignore?.mode === 'count' && state.ignore.count !== null) {
    parts.push(
      t('issues.state.line.ignoredCount', {
        count: state.ignore.count,
        seen: number(state.ignore.seen),
      }),
    )
  } else if (state.ignore?.mode === 'until' && state.ignore.until) {
    parts.push(
      t('issues.state.line.ignoredUntil', {
        when: date(state.ignore.until, {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        }),
      }),
    )
  } else parts.push(t('issues.state.line.ignored'))
  // Members still loading (or the sandbox, which has none to show): the assignee waits.
  if (state?.assignee_id && members.names.size > 0)
    parts.push(
      t('issues.state.line.assigned', {
        name: members.names.get(state.assignee_id) ?? t('issues.state.formerMember'),
      }),
    )
  return parts
}

/** What the status select sets: the two pickers ("…") open below the bar. */
type StatusChoice = 'unresolved' | 'next' | 'release' | 'ignored'

/**
 * The issue page's action bar (design "3 Issue", APS-390): a status select of what can be set
 * (Unresolved reopens; "Resolved in release…" and "Ignored…" open their pickers), Resolve as the
 * one click to the next release (also the `e` key), Assign (`a`), then the page's own actions
 * (Hand off to agent, Copy for agent).
 */
export function IssueActions({
  orgId,
  projectId,
  issueId,
  appId,
  state,
  members,
  extra,
}: {
  orgId: string
  projectId: string
  issueId: string
  appId: string
  state: IssueState | undefined
  members: Members
  /** More actions at the end of the row (Copy for agent). */
  extra?: ReactNode
}) {
  const { t } = useI18n()
  const set = useSetIssueState(projectId)
  const [picker, setPicker] = useState<'release' | 'ignored' | null>(null)
  const status = state?.status ?? 'unresolved'
  const change = (c: IssueStateChange) => set.mutate({ issueId, change: c })
  const resolveNext = useRef(() => {})
  resolveNext.current = () => {
    if (status !== 'resolved' && !set.isPending) change(RESOLVE_NEXT)
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'e' || shortcutOff(e)) return
      e.preventDefault()
      resolveNext.current()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  const value: StatusChoice =
    status === 'resolved'
      ? state?.resolution === 'in_release'
        ? 'release'
        : 'next'
      : status === 'ignored'
        ? 'ignored'
        : 'unresolved'
  const choose = (choice: StatusChoice) => {
    setPicker(null)
    if (choice === 'unresolved') change(REOPEN)
    else if (choice === 'next') change(RESOLVE_NEXT)
    else setPicker(choice)
  }
  const statusId = useId()

  return (
    <>
      <fieldset className="is-acts">
        <legend className="visually-hidden">{t('issue.actions.label')}</legend>
        {/* What can be set: a new, regressed or ongoing issue reads Unresolved here, and its
            derived status stays the word in the meta (Mosaic, APS-390). */}
        <label className="vh" htmlFor={statusId}>
          {t('issue.status.label')}
        </label>
        <select
          id={statusId}
          className="select is-state"
          value={value}
          disabled={set.isPending}
          onChange={(e) => choose(e.target.value as StatusChoice)}
        >
          <option value="unresolved">{t('issue.status.unresolved')}</option>
          <option value="next">{t('issue.status.next')}</option>
          <option value="release">
            {value === 'release' && state?.resolved_release
              ? t('issue.status.inRelease', { release: state.resolved_release })
              : t('issue.status.release')}
          </option>
          <option value="ignored">
            {value === 'ignored' ? t('issue.status.ignoredNow') : t('issue.status.ignored')}
          </option>
        </select>
        {status !== 'resolved' ? (
          <button
            type="button"
            className="btn"
            aria-keyshortcuts="e"
            disabled={set.isPending}
            onClick={() => change(RESOLVE_NEXT)}
          >
            <Icon name="check" />
            {/* The one-click resolve: its name says where to and the visible word starts it. The
                name is hidden text, not an aria-label, which would have to hold the key hint
                too (WCAG 2.5.3, axe 4.14). */}
            <span aria-hidden="true">{t('issue.actions.resolveShort')}</span>
            <span className="vh">{t('issue.actions.resolveNext')}</span>{' '}
            <span aria-hidden="true">
              <kbd translate="no">e</kbd>
            </span>
          </button>
        ) : null}
        <ActionMenu
          small={false}
          shortcut="a"
          label={
            state?.assignee_id && members.names.get(state.assignee_id) ? (
              <span className="is-who" translate="no">
                <span className="vh">{`${t('issue.actions.assign')}: `}</span>
                <span className="avatar" aria-hidden="true">
                  {initials(members.names.get(state.assignee_id) ?? '', '')}
                </span>
                {members.names.get(state.assignee_id)}
              </span>
            ) : (
              t('issue.actions.assign')
            )
          }
          disabled={set.isPending}
          items={[
            ...members.list.map((m) => ({
              key: m.id,
              label: m.name,
              verbatim: true,
              onSelect: () => change({ assignee_id: m.id }),
            })),
            ...(state?.assignee_id
              ? [
                  {
                    key: 'none',
                    label: t('issue.actions.unassign'),
                    onSelect: () => change({ assignee_id: null }),
                  },
                ]
              : []),
          ]}
        />
        <span className="is-break" aria-hidden="true" />
        {extra}
      </fieldset>
      {picker === 'release' ? (
        <ResolveInRelease
          orgId={orgId}
          projectId={projectId}
          appId={appId}
          pending={set.isPending}
          onCancel={() => setPicker(null)}
          onResolve={(app, release) =>
            set.mutate(
              {
                issueId,
                change: { status: 'resolved', resolution: 'in_release', app_id: app, release },
              },
              { onSuccess: () => setPicker(null) },
            )
          }
        />
      ) : picker === 'ignored' ? (
        <IgnoreFor
          pending={set.isPending}
          onCancel={() => setPicker(null)}
          onIgnore={(preset) =>
            set.mutate(
              { issueId, change: ignoreChange(preset) },
              { onSuccess: () => setPicker(null) },
            )
          }
        />
      ) : null}
      {set.isError ? (
        <p role="alert" className="notice error">
          {stateErrorMessage(t, set.error)}
        </p>
      ) : null}
    </>
  )
}

/** Choose how long to ignore an issue for: the status select's "Ignored…". */
function IgnoreFor({
  pending,
  onCancel,
  onIgnore,
}: {
  pending: boolean
  onCancel: () => void
  onIgnore: (preset: IgnorePreset) => void
}) {
  const { t } = useI18n()
  const [key, setKey] = useState(IGNORE_PRESETS[0]?.key ?? 'forever')
  const id = useId()
  const submit = (e: FormEvent) => {
    e.preventDefault()
    const chosen = IGNORE_PRESETS.find((p) => p.key === key)
    if (chosen) onIgnore(chosen.preset)
  }
  return (
    <form className="win" onSubmit={submit}>
      <div className="win-bar">
        <b>{t('issue.actions.ignoreTitle')}</b>
      </div>
      <fieldset className="is-resolve-in" style={{ border: 0, margin: 0, minWidth: 0 }}>
        <legend className="visually-hidden">{t('issue.actions.ignoreTitle')}</legend>
        <div className="field">
          <label htmlFor={id}>{t('issue.actions.ignoreFor')}</label>
          <select id={id} className="select" value={key} onChange={(e) => setKey(e.target.value)}>
            {IGNORE_PRESETS.map(({ key: k, preset }) => (
              <option key={k} value={k}>
                {ignoreLabel(t, preset)}
              </option>
            ))}
          </select>
        </div>
        <span style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <button type="submit" className="btn solid" disabled={pending}>
            {t('issue.actions.ignore')}
          </button>
          <button type="button" className="btn ghost" onClick={onCancel}>
            {t('issue.actions.cancel')}
          </button>
        </span>
      </fieldset>
    </form>
  )
}

/** The window the status select's "Resolved in release…" offers releases from. */
const RELEASE_DAYS = 90

/** Choose an app of the project, then one of its releases, newest first. */
function ResolveInRelease({
  orgId,
  projectId,
  appId,
  pending,
  onCancel,
  onResolve,
}: {
  orgId: string
  projectId: string
  appId: string
  pending: boolean
  onCancel: () => void
  onResolve: (appId: string, release: string) => void
}) {
  const { t } = useI18n()
  const [now] = useState(() => Math.floor(Date.now() / 60_000) * 60_000)
  const releases = useQuery(windowReleasesQuery(projectId, RELEASE_DAYS, now))
  const apps = useQuery(appsQuery(orgId, projectId))
  const [app, setApp] = useState(appId)
  const [release, setRelease] = useState('')
  const appSelect = useId()
  const releaseSelect = useId()
  const appIds = [...new Set([appId, ...(releases.data ?? []).map((r) => r.app_id)])]
  const list = releasesNewestFirst(releases.data ?? [], app)
  const chosen = release && list.some((r) => r.release === release) ? release : list[0]?.release
  const appName = (id: string) => apps.data?.find((a) => a.id === id)?.name ?? id

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (chosen) onResolve(app, chosen)
  }
  return (
    <form className="win" onSubmit={submit}>
      <div className="win-bar">
        <b>{t('issue.actions.resolveInTitle')}</b>
      </div>
      <fieldset className="is-resolve-in" style={{ border: 0, margin: 0, minWidth: 0 }}>
        <legend className="visually-hidden">{t('issue.actions.resolveInTitle')}</legend>
        <div className="field">
          <label htmlFor={appSelect}>{t('issue.actions.app')}</label>
          <select
            id={appSelect}
            className="select"
            value={app}
            onChange={(e) => {
              setApp(e.target.value)
              setRelease('')
            }}
          >
            {appIds.map((id) => (
              <option key={id} value={id} translate="no">
                {appName(id)}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor={releaseSelect}>{t('issue.actions.release')}</label>
          <select
            id={releaseSelect}
            className="select"
            value={chosen ?? ''}
            disabled={!list.length}
            onChange={(e) => setRelease(e.target.value)}
          >
            {list.map((r) => (
              <option key={r.release} value={r.release} translate="no">
                {r.release}
              </option>
            ))}
          </select>
        </div>
        <span style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <button type="submit" className="btn solid" disabled={!chosen || pending}>
            {t('issue.actions.resolve')}
          </button>
          <button type="button" className="btn ghost" onClick={onCancel}>
            {t('issue.actions.cancel')}
          </button>
        </span>
      </fieldset>
      {releases.isPending ? (
        <p className="note" style={{ padding: '0 14px 12px' }}>
          {t('state.loading')}
        </p>
      ) : !list.length ? (
        <p className="note" style={{ padding: '0 14px 12px' }}>
          {t('issue.actions.noReleases', { days: RELEASE_DAYS })}
        </p>
      ) : null}
    </form>
  )
}
